import { EmpiricaClassic } from "@empirica/core/player/classic";
import { EmpiricaContext } from "@empirica/core/player/classic/react";
import { EmpiricaMenu, EmpiricaParticipant } from "@empirica/core/player/react";
import React, { useEffect } from "react";
import { Game } from "./Game";
import { ExitSurvey } from "./intro-exit/ExitSurvey";
import { CompletionScreen } from "./intro-exit/CompletionScreen";
import { Consent } from "./intro-exit/Consent";
import { InstructionStepOne } from "./intro-exit/InstructionStepOne";
import { DomainRanking } from "./intro-exit/DomainRanking";
import { DevModeSelection } from "./intro-exit/DevModeSelection";
import { Sorry } from "./intro-exit/Sorry";
import { NoExperiments } from "@/components/screens/NoExperiments";
import { WaitingRoom } from "@/components/screens/WaitingRoom";

// Generate or retrieve a persistent player key
function getPlayerKey() {
  const urlParams = new URLSearchParams(window.location.search);

  // Check URL params first
  const urlKey = urlParams.get("participantKey") || urlParams.get("PROLIFIC_PID");
  if (urlKey) {
    // Check for session/batch parameter (allows same participant to join multiple sessions)
    const session = urlParams.get("session") || urlParams.get("batch");
    // Check for retry parameter (used when player restarts after partner dropout)
    const retry = urlParams.get("retry");

    let key = urlKey;
    if (session) {
      key = `${key}_s${session}`;
    }
    if (retry) {
      key = `${key}_retry_${retry}`;
    }
    return key;
  }

  // Check sessionStorage for existing key
  const storedKey = sessionStorage.getItem("empirica_player_key");
  if (storedKey) return storedKey;

  // Generate new random key and store it
  const newKey = `player_${Math.random().toString(36).substring(2, 11)}`;
  sessionStorage.setItem("empirica_player_key", newKey);
  return newKey;
}

// Auto-submit player ID to skip the identifier form
function AutoPlayerCreate({ onPlayerID, connecting }) {
  useEffect(() => {
    if (!connecting) {
      const playerKey = getPlayerKey();
      onPlayerID(playerKey);
    }
  }, [onPlayerID, connecting]);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
        <p className="text-gray-600">Connecting...</p>
      </div>
    </div>
  );
}

export default function App() {
  const playerKey = getPlayerKey();

  const { protocol, host } = window.location;
  const url = `${protocol}//${host}/query`;

  function introSteps({ game }) {
    // Check if this is a dev mode game (DEV treatment or ?dev=true URL param)
    const urlParams = new URLSearchParams(window.location.search);
    const isDevUrl = urlParams.get("dev") === "true";
    const treatment = game?.get("treatment") || {};
    const isDevTreatment = treatment.devMode === true || treatment.devSelectMode === true;
    const isDevMode = isDevUrl || isDevTreatment;

    if (isDevMode) {
      // Dev mode: Skip consent/instructions, just show topic selection
      console.log("[introSteps] Dev mode detected - showing DevModeSelection");
      return [DevModeSelection];
    }

    // Production flow: Consent → Instructions (with Attention Check) → Domain Ranking
    // Domain ranking is used for role assignment (more familiar = Teacher, less familiar = Student)
    // Familiarity survey moved into game (Round 0)
    const steps = [Consent, InstructionStepOne, DomainRanking];
    return steps;
  }

  function exitSteps({ player, game }) {
    // Check if player should see Sorry screen instead of survey
    // This happens when: lobby timeout, partner dropout, game full, etc.
    // Use .get() for custom properties set via player.set()
    const exitStatus = player?.get("exitStatus");
    const exitReason = player?.get("exitReason");
    const devMode = game?.get("devMode");
    const hasQuiz = player?.get("comprehension_quiz") !== undefined;

    console.log("[exitSteps] Called with:", { exitStatus, exitReason, devMode, hasQuiz, playerId: player?.id });

    // Don't block players who were exited from WRONG game - they need to return to correct game
    // Return empty array so they can re-enter their committed game
    if (exitStatus === "wrongGameContext") {
      console.log("[exitSteps] wrongGameContext detected - allowing re-entry to correct game");
      return [];
    }

    if (
      exitStatus &&
      exitStatus !== "finished" &&
      exitReason !== "playerQuit"
    ) {
      return [Sorry];
    }

    // In dev mode, skip completion checks - allows testing with DevControls
    // without being blocked by missing quiz data
    if (devMode) {
      console.log("[exitSteps] Dev mode - skipping completion check");
      return [ExitSurvey, CompletionScreen];
    }

    // Safety check: only show survey if player actually completed the game
    // (has quiz answers = reached the final round)
    const hasCompletedGame = player?.get("comprehension_quiz") !== undefined;
    if (!hasCompletedGame) {
      // Player didn't complete the game - show Sorry screen
      // This catches edge cases like batch closed mid-game
      return [Sorry];
    }

    // Exit flow: Exit Survey → Completion Screen (with code)
    return [ExitSurvey, CompletionScreen];
  }

  return (
    <EmpiricaParticipant url={url} ns={playerKey} modeFunc={EmpiricaClassic}>
      <div className="h-screen relative">
        <EmpiricaMenu position="bottom-left" />
        <div className="h-full overflow-auto">
          <EmpiricaContext
            playerCreate={AutoPlayerCreate}
            introSteps={introSteps}
            exitSteps={exitSteps}
            noGames={NoExperiments}
            lobby={WaitingRoom}
          >
            <Game />
          </EmpiricaContext>
        </div>
      </div>
    </EmpiricaParticipant>
  );
}
